// Deck-editor card data, fetched on demand from the world server's /cards API and
// cached by TanStack Query (24h). Only the page / ids the user is viewing are
// fetched, the full ~14k catalog never ships to the client.

import type { CardInfo } from "@ygoevo/yo2-data";
import aliasesData from "@ygoevo/yo2-data/generated/aliases.json";
import { api } from "./config.ts";

// Alt-art / reprint / duplicate-token passcode → its canonical (base-art) passcode.
// The duel board reports each token its own real code (e.g. the 4 Sheep Tokens are
// 73915052-55, where 53-55 alias to 52); fold to the base so name + art resolve to
// the one entry that carries them — exactly how EDOPro shows tokens.
const ALIASES = aliasesData.aliases as Record<string, number>;
export const canonicalCode = (code: number): number => ALIASES[code] ?? code;

export interface SearchParams {
  q?: string;
  cat?: string;
  attr?: string;
  level?: number;
  race?: string;
  type?: string;
  offset?: number;
  limit?: number;
}

export interface SearchResult {
  total: number;
  offset: number;
  cards: CardInfo[];
}

export async function searchCards(p: SearchParams): Promise<SearchResult> {
  const sp = new URLSearchParams();
  if (p.q) sp.set("q", p.q);
  if (p.cat) sp.set("cat", p.cat);
  if (p.attr) sp.set("attr", p.attr);
  if (p.level != null) sp.set("level", String(p.level));
  if (p.race) sp.set("race", p.race);
  if (p.type) sp.set("type", p.type);
  sp.set("offset", String(p.offset ?? 0));
  sp.set("limit", String(p.limit ?? 60));
  const r = await fetch(api(`/cards/search?${sp.toString()}`));
  if (!r.ok) throw new Error(`card search failed: ${r.status}`);
  return r.json() as Promise<SearchResult>;
}

export async function cardsByIds(ids: number[]): Promise<CardInfo[]> {
  const uniq = [...new Set(ids)].filter((n) => Number.isFinite(n));
  if (!uniq.length) return [];
  // Chunk so no single GET URL blows a server/proxy length cap. A full collection is thousands of
  // ids, and `?ids=a,b,c,…` past ~8KB is rejected by Node's max-header-size / a proxy BEFORE
  // the handler attaches its CORS header, which the browser surfaces as a misleading CORS + ERR_FAILED
  // (reported 2026-07-10: deck editor "Couldn't load cards" once a collection grew large enough).
  // ~400 ids ≈ 3.6KB URL — safely under the most conservative 8KB limit.
  const CHUNK = 400;
  const batches: number[][] = [];
  for (let i = 0; i < uniq.length; i += CHUNK) batches.push(uniq.slice(i, i + CHUNK));
  const pages = await Promise.all(
    batches.map(async (batch) => {
      const r = await fetch(api(`/cards/byid?ids=${batch.join(",")}`));
      if (!r.ok) throw new Error(`card lookup failed: ${r.status}`);
      return (await r.json()).cards as CardInfo[];
    }),
  );
  return pages.flat();
}

/** Card-frame colour by frame type (used by text-frame fallbacks). */
export function frameColor(card: CardInfo): string {
  if (card.category === "spell") return "#1aa088";
  if (card.category === "trap") return "#b5417f";
  const t = card.types;
  if (t.includes("Link")) return "#3577d4";
  if (t.includes("Xyz")) return "#2b2f38";
  if (t.includes("Synchro")) return "#c9ccd2";
  if (t.includes("Fusion")) return "#7d54b6";
  if (t.includes("Ritual")) return "#4f7fc0";
  if (t.includes("Normal")) return "#c8a04e";
  return "#bb7a36";
}

/** Compact type line, e.g. "DARK · Spellcaster · Effect / Synchro". */
export function typeLine(c: CardInfo): string {
  if (c.category === "spell" || c.category === "trap") {
    return [c.types.join(" / ") || c.category.toUpperCase()].join("");
  }
  return [c.attribute, c.race, c.types.join(" / ")].filter(Boolean).join(" · ");
}

/** Client-side fuzzy filter over a small in-memory set (the player's collection).
 *  Normalises punctuation/spacing so "blueeyes"/"blue eyes" both match, requires
 *  all query tokens, matches the card's effect text too (not everyone knows names),
 *  and ranks name matches above text-only matches. */
export function filterCards(
  cards: CardInfo[],
  f: { q?: string; cat?: string; attr?: string; level?: number; race?: string; type?: string },
): CardInfo[] {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  let r = cards;
  if (f.cat) r = r.filter((c) => c.category === f.cat);
  if (f.attr) r = r.filter((c) => c.attribute === f.attr);
  if (f.race) r = r.filter((c) => c.race === f.race);
  if (f.type) r = r.filter((c) => c.types.includes(f.type!));
  if (f.level != null) r = r.filter((c) => c.level === f.level);
  if (f.q && f.q.trim()) {
    const nq = norm(f.q);
    const tokens = f.q.toLowerCase().split(/\s+/).map(norm).filter(Boolean);
    const nameHit = (c: CardInfo) => { const n = norm(c.name); return n.includes(nq) || tokens.every((t) => n.includes(t)); };
    const textHit = (c: CardInfo) => { const d = norm(c.desc); return tokens.every((t) => d.includes(t)); };
    r = r.filter((c) => nameHit(c) || textHit(c));
    // prefix-on-name → name match → text-only match, then alphabetical.
    const rank = (c: CardInfo) => (norm(c.name).startsWith(nq) ? 0 : nameHit(c) ? 1 : 2);
    return [...r].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }
  return [...r].sort((a, b) => a.name.localeCompare(b.name));
}

/** Stable query key for a search request. */
export const searchKey = (p: SearchParams) => ["cards", "search", p] as const;
export const byIdKey = (ids: number[]) =>
  ["cards", "byid", [...new Set(ids)].sort((a, b) => a - b)] as const;
